import 'client-only';

import { useBrand } from '@curology/ui-components-web/brand/context';
import braintree, {
  type DataCollector,
  type PayPalCheckout,
} from 'braintree-web';
import { atom, useAtom } from 'jotai';
import { useEffect } from 'react';

import { type Brand } from '@/consts/brands';
import { APP_ENV } from '@/consts/environmental';
import { PAYPAL_CONSTANTS } from '@/consts/payments';
import { PayPalService } from '@/services/productSelectionAndCheckoutService/payPal';

const braintreeClientsAtom = atom<{
  payPalCheckout: PayPalCheckout;
  dataCollector: DataCollector;
} | null>(null);
const braintreeLoadingAtom = atom<boolean>(true);

const initializeClients = async (brand: Brand) => {
  const response = await PayPalService.getBraintreeToken({ brand });
  const token = response.data.braintreeToken;
  const client = await braintree.client.create({
    authorization: token,
  });

  const [payPalCheckout, dataCollector] = await Promise.all([
    braintree.paypalCheckout.create({ client: client }),
    braintree.dataCollector.create({ client: client, paypal: true }),
  ]);

  return {
    payPalCheckout,
    dataCollector,
  };
};

let braintreeClientsPromise: ReturnType<typeof initializeClients> | null = null;

/**
 * The Braintree clients can be used to collect device data and to turn the authorized
 * PayPal account into a Braintree token response.
 */
export const useBraintree = (onError: (message: string) => void = () => {}) => {
  const brand = useBrand();
  const [clients, setClients] = useAtom(braintreeClientsAtom);
  const [loading, setLoading] = useAtom(braintreeLoadingAtom);

  useEffect(() => {
    if (braintreeClientsPromise) {
      return;
    }

    braintreeClientsPromise = initializeClients(brand);
    braintreeClientsPromise
      .then((clients) => {
        setClients(clients);
        setLoading(false);
      })
      .catch(() => {
        onError(
          'Error connecting to the PayPal payment processor. Please refresh and try again.',
        );
      });
  }, [brand, clients, setClients, onError, loading, setLoading]);

  return {
    clients,
    env:
      APP_ENV === 'production'
        ? PAYPAL_CONSTANTS.PRODUCTION
        : PAYPAL_CONSTANTS.SANDBOX,
  };
};
